import type { ToolCallTimelineItem } from "../server/agent/agent-sdk-types.js";
import {
  getAppostleToolLeafName,
  isAppostleToolName,
} from "../server/agent/tool-name-normalization.js";
import { stripCwdPrefix } from "./path-utils.js";

export type ToolCallDisplayInput = Pick<
  ToolCallTimelineItem,
  "name" | "status" | "error" | "metadata" | "detail"
> & {
  cwd?: string;
};

export type ToolCallDisplayModel = {
  displayName: string;
  summary?: string;
  errorText?: string;
  /**
   * For tool calls that came in via the MCP namespacing convention
   * (`mcp__<server>__<tool>`), this is the literal `<server>` segment —
   * stripped of the `mcp__` prefix. The client uses it to render a
   * server-specific glyph next to the label (e.g. a Chrome icon for
   * `claude-in-chrome`). Undefined for non-MCP tools.
   */
  mcpServer?: string;
};

type DetailDisplay = {
  displayName?: string;
  summary?: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

export function humanizeToolName(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) {
    return name;
  }
  if (isAppostleToolName(trimmed)) {
    const leaf = getAppostleToolLeafName(trimmed);
    if (leaf) {
      return humanizeToolName(leaf);
    }
  }
  // MCP tools follow `mcp__<server>__<tool>` (or longer) — strip the prefix
  // and namespace, take the leaf, and humanize it like a regular tool name.
  // Without this, every MCP tool (e.g. `mcp__claude-in-chrome__tabs_context_mcp`)
  // would render as the raw underscored namespace and look "exotic".
  if (trimmed.includes("__")) {
    const segments = trimmed.split("__").filter((s) => s.length > 0);
    const leaf = segments[segments.length - 1];
    if (leaf && leaf.length > 0) {
      return humanizeToolName(leaf);
    }
  }
  if (/[:./]/.test(trimmed)) {
    return trimmed;
  }

  return trimmed
    .replace(/[._-]+/g, " ")
    .split(" ")
    .filter((segment) => segment.length > 0)
    .map((segment) => `${segment[0]?.toUpperCase() ?? ""}${segment.slice(1)}`)
    .join(" ");
}

function formatErrorText(error: unknown): string | undefined {
  if (error === null || error === undefined) {
    return undefined;
  }
  if (typeof error === "string") {
    return error;
  }
  if (isRecord(error) && typeof error.content === "string") {
    return error.content;
  }
  try {
    return JSON.stringify(error, null, 2);
  } catch {
    return String(error);
  }
}

function buildFilePathDisplay(
  displayName: string,
  filePath: string,
  cwd: string | undefined,
): DetailDisplay {
  return {
    displayName,
    summary: stripCwdPrefix(filePath, cwd),
  };
}

function buildCanonicalDetailDisplay(input: ToolCallDisplayInput): DetailDisplay {
  switch (input.detail.type) {
    case "shell":
      return {
        displayName: "Shell",
        summary: input.detail.command,
      };
    case "read":
      return buildFilePathDisplay("Read", input.detail.filePath, input.cwd);
    case "edit":
      return buildFilePathDisplay("Edit", input.detail.filePath, input.cwd);
    case "write":
      return buildFilePathDisplay("Write", input.detail.filePath, input.cwd);
    case "search":
      return {
        displayName: "Search",
        summary: input.detail.query,
      };
    case "fetch":
      return {
        displayName: "Fetch",
        summary: input.detail.url,
      };
    case "worktree_setup":
      return {
        displayName: "Worktree Setup",
        summary: input.detail.branchName,
      };
    case "sub_agent":
      return {
        displayName: readString(input.detail.subAgentType) ?? "Task",
        summary: readString(input.detail.description),
      };
    case "plain_text":
      return {
        summary: input.detail.label,
      };
    case "plan":
      return {
        displayName: "Plan",
      };
    case "image":
      return {
        displayName: input.detail.toolName ? humanizeToolName(input.detail.toolName) : "Image",
      };
    case "unknown":
      return {};
  }
}

function buildUnknownDetailOverride(input: ToolCallDisplayInput): DetailDisplay {
  const lowerName = input.name.trim().toLowerCase();
  if (input.detail.type === "unknown" && lowerName === "task") {
    return {
      displayName: "Task",
      summary: isRecord(input.metadata) ? readString(input.metadata.subAgentActivity) : undefined,
    };
  }
  if (input.detail.type === "unknown" && lowerName === "thinking") {
    return {
      displayName: "Thinking",
    };
  }
  if (lowerName === "terminal") {
    return {
      displayName: "Terminal",
      summary: input.detail.type === "plain_text" ? readString(input.detail.label) : undefined,
    };
  }
  return {};
}

/**
 * Extracts the MCP server name from a namespaced tool name. MCP tool names
 * follow `mcp__<server>__<tool>` (e.g. `mcp__claude-in-chrome__navigate`).
 * Returns the `<server>` segment, or undefined if the name doesn't match.
 */
function extractMcpServer(rawName: string): string | undefined {
  const trimmed = rawName.trim();
  if (!trimmed.startsWith("mcp__")) return undefined;
  const segments = trimmed.split("__").filter((s) => s.length > 0);
  // segments[0] === "mcp", segments[1] === server, segments[2..] === tool path
  if (segments.length < 3) return undefined;
  const server = segments[1]?.trim();
  return server && server.length > 0 ? server : undefined;
}

export function buildToolCallDisplayModel(input: ToolCallDisplayInput): ToolCallDisplayModel {
  const canonicalDisplay = buildCanonicalDetailDisplay(input);
  const unknownDetailOverride = buildUnknownDetailOverride(input);
  const displayName =
    unknownDetailOverride.displayName ??
    canonicalDisplay.displayName ??
    humanizeToolName(input.name);
  const summary = unknownDetailOverride.summary ?? canonicalDisplay.summary;
  const errorText = input.status === "failed" ? formatErrorText(input.error) : undefined;
  const mcpServer = extractMcpServer(input.name);

  return {
    displayName,
    ...(summary ? { summary } : {}),
    ...(errorText ? { errorText } : {}),
    ...(mcpServer ? { mcpServer } : {}),
  };
}
